import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus,
  Trash2,
  Ban,
  Search,
  AlertCircle,
  Phone,
  Edit,
  Car,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Client } from '@/types/client';
import {
  BlockedContact,
  BlockedContactCreateRequest,
  BlockedContactUpdateRequest,
  BlockScopeType,
} from '@/types/blockedContact';
import {
  BlockedVehicle,
  BlockedVehicleCreateRequest,
  BlockedVehicleUpdateRequest,
} from '@/types/blockedVehicle';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { apiService } from '@/lib/api';

const BLOCK_SCOPE_OPTIONS: { value: BlockScopeType; label: string }[] = [
  { value: 'LEMBRETE', label: 'Lembrete (até o vencimento)' },
  { value: 'COBRANCA', label: 'Cobrança (pós-vencimento)' },
  { value: 'REVISTORIA', label: 'Revistoria' },
];

function ScopeCheckboxes({
  value,
  onChange,
  idPrefix,
}: {
  value: BlockScopeType[] | null | undefined;
  onChange: (scopes: BlockScopeType[] | null) => void;
  idPrefix: string;
}) {
  const selected = value && value.length > 0 ? value : [];
  const toggle = (scope: BlockScopeType) => {
    const next = selected.includes(scope)
      ? selected.filter(s => s !== scope)
      : [...selected, scope];
    onChange(next.length === 0 ? null : next);
  };
  return (
    <div className="space-y-2">
      <Label>Bloquear para (opcional)</Label>
      <p className="text-xs text-gray-500 mb-2">
        Deixe em branco para bloquear em todos os tipos de envio.
      </p>
      <div className="flex flex-wrap gap-4">
        {BLOCK_SCOPE_OPTIONS.map(opt => (
          <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id={`${idPrefix}-${opt.value}`}
              checked={selected.includes(opt.value)}
              onCheckedChange={() => toggle(opt.value)}
            />
            <span className="text-sm">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function formatScopesDisplay(scopes: BlockScopeType[] | null | undefined): string {
  if (!scopes || scopes.length === 0) return 'Todos os tipos';
  const labels: Record<BlockScopeType, string> = {
    LEMBRETE: 'Lembrete',
    COBRANCA: 'Cobrança',
    REVISTORIA: 'Revistoria',
  };
  return scopes.map(s => labels[s]).join(', ');
}

interface BlockedContactsTabProps {
  client: Client | null;
}

const BlockedContactsTab: React.FC<BlockedContactsTabProps> = ({ client }) => {
  const [blockedContacts, setBlockedContacts] = useState<BlockedContact[]>([]);
  const [blockedVehicles, setBlockedVehicles] = useState<BlockedVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingContact, setEditingContact] = useState<BlockedContact | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<BlockedVehicle | null>(null);
  const contactFormRef = useRef<HTMLDivElement>(null);
  const vehicleFormRef = useRef<HTMLDivElement>(null);

  const [contactFormData, setContactFormData] = useState<BlockedContactCreateRequest>({
    phone_number: '',
    reason: '',
    block_scopes: null,
  });

  const [vehicleFormData, setVehicleFormData] = useState<BlockedVehicleCreateRequest>({
    chassi: '',
    reason: null,
    block_scopes: null,
  });

  useEffect(() => {
    if (client?.id) {
      loadBlockedContacts();
      loadBlockedVehicles();
    }
  }, [client?.id]);

  const loadBlockedContacts = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const response = await apiService.getBlockedContacts(client.id);
      setBlockedContacts(response.data || []);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Erro ao carregar contatos bloqueados',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBlockedVehicles = async () => {
    if (!client?.id) return;
    try {
      const response = await apiService.getBlockedVehicles(client.id);
      setBlockedVehicles(response.data || []);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Erro ao carregar veículos bloqueados',
        variant: 'destructive',
      });
    }
  };

  const resetContactForm = () => {
    setContactFormData({ phone_number: '', reason: '', block_scopes: null });
    setShowContactForm(false);
    setEditingContact(null);
  };

  const resetVehicleForm = () => {
    setVehicleFormData({ chassi: '', reason: null, block_scopes: null });
    setShowVehicleForm(false);
    setEditingVehicle(null);
  };

  const handleEditContact = (contact: BlockedContact) => {
    setEditingContact(contact);
    setContactFormData({
      phone_number: contact.phone_number,
      reason: contact.reason || '',
      block_scopes: contact.block_scopes ?? null,
    });
    setShowContactForm(true);
    setTimeout(() => contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  const handleEditVehicle = (vehicle: BlockedVehicle) => {
    setEditingVehicle(vehicle);
    setVehicleFormData({
      chassi: vehicle.chassi,
      reason: vehicle.reason ?? null,
      block_scopes: vehicle.block_scopes ?? null,
    });
    setShowVehicleForm(true);
    setTimeout(() => vehicleFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client?.id) return;
    if (!contactFormData.phone_number.trim()) {
      toast({ title: 'Erro', description: 'Número de telefone é obrigatório', variant: 'destructive' });
      return;
    }
    if (contactFormData.phone_number.replace(/\D/g, '').length < 10) {
      toast({ title: 'Erro', description: 'Número de telefone inválido (mínimo 10 dígitos)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingContact) {
        const updateData: BlockedContactUpdateRequest = {
          ...(contactFormData.phone_number !== editingContact.phone_number && { phone_number: contactFormData.phone_number }),
          ...(contactFormData.reason !== (editingContact.reason || '') && { reason: contactFormData.reason || undefined }),
          block_scopes: contactFormData.block_scopes ?? undefined,
        };
        if (Object.keys(updateData).length > 0) {
          await apiService.updateBlockedContact(client.id, editingContact.id, updateData);
          toast({ title: 'Sucesso', description: 'Contato bloqueado atualizado com sucesso' });
        }
      } else {
        await apiService.addBlockedContact(client.id, {
          phone_number: contactFormData.phone_number,
          reason: contactFormData.reason || undefined,
          block_scopes: contactFormData.block_scopes ?? undefined,
        });
        toast({ title: 'Sucesso', description: 'Contato bloqueado adicionado com sucesso' });
      }
      resetContactForm();
      loadBlockedContacts();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Erro ao salvar',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client?.id) return;
    const chassi = vehicleFormData.chassi.trim();
    if (!chassi) {
      toast({ title: 'Erro', description: 'Chassi é obrigatório', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingVehicle) {
        const updateData: BlockedVehicleUpdateRequest = {
          ...(chassi !== editingVehicle.chassi && { chassi }),
          reason: vehicleFormData.reason ?? undefined,
          block_scopes: vehicleFormData.block_scopes ?? undefined,
        };
        await apiService.updateBlockedVehicle(client.id, editingVehicle.id, updateData);
        toast({ title: 'Sucesso', description: 'Veículo bloqueado atualizado com sucesso' });
      } else {
        await apiService.addBlockedVehicle(client.id, {
          chassi,
          reason: vehicleFormData.reason ?? undefined,
          block_scopes: vehicleFormData.block_scopes ?? undefined,
        });
        toast({ title: 'Sucesso', description: 'Veículo bloqueado adicionado com sucesso' });
      }
      resetVehicleForm();
      loadBlockedVehicles();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Erro ao salvar',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    if (!client?.id || !confirm('Remover este contato da lista de bloqueados?')) return;
    try {
      await apiService.removeBlockedContact(client.id, contactId);
      toast({ title: 'Sucesso', description: 'Contato removido da lista de bloqueados' });
      loadBlockedContacts();
    } catch (error: any) {
      toast({ title: 'Erro', description: error?.response?.data?.error || error?.message || 'Erro ao remover', variant: 'destructive' });
    }
  };

  const handleDeleteVehicle = async (vehicleId: number) => {
    if (!client?.id || !confirm('Remover este veículo da lista de bloqueados?')) return;
    try {
      await apiService.removeBlockedVehicle(client.id, vehicleId);
      toast({ title: 'Sucesso', description: 'Veículo removido da lista de bloqueados' });
      loadBlockedVehicles();
    } catch (error: any) {
      toast({ title: 'Erro', description: error?.response?.data?.error || error?.message || 'Erro ao remover', variant: 'destructive' });
    }
  };

  const filteredContacts = blockedContacts.filter(c => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return c.phone_number.toLowerCase().includes(s) || (c.reason && c.reason.toLowerCase().includes(s));
  });

  const filteredVehicles = blockedVehicles.filter(v => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return v.chassi.toLowerCase().includes(s) || (v.reason && v.reason.toLowerCase().includes(s));
  });

  if (loading && blockedContacts.length === 0 && blockedVehicles.length === 0) {
    return <AtomicLoadingScreen message="Carregando bloqueios..." />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Contatos e veículos bloqueados</h2>
        <p className="text-sm text-gray-600 mt-1">
          Gerencie números de telefone e veículos (chassi) que não receberão envios, por tipo: lembretes, cobranças ou revistoria.
        </p>
      </div>

      {/* Seção: Por telefone */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Por telefone
            </CardTitle>
            <CardDescription>Números que não receberão envios (com escopo opcional)</CardDescription>
          </div>
          <Button
            onClick={() => {
              if (editingContact) resetContactForm();
              else {
                setEditingContact(null);
                setContactFormData({ phone_number: '', reason: '', block_scopes: null });
                setShowContactForm(!showContactForm);
              }
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {showContactForm && !editingContact ? 'Cancelar' : 'Adicionar contato'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showContactForm && (
            <div ref={contactFormRef}>
              <form onSubmit={handleSubmitContact} className="space-y-4 p-4 border rounded-lg bg-gray-50/50">
                <div>
                  <Label htmlFor="phone_number">Número de telefone *</Label>
                  <Input
                    id="phone_number"
                    type="tel"
                    placeholder="Ex: (81) 99999-9999 ou 5581999999999"
                    value={contactFormData.phone_number}
                    onChange={e => setContactFormData({ ...contactFormData, phone_number: e.target.value })}
                    required
                  />
                </div>
                <ScopeCheckboxes
                  idPrefix="contact"
                  value={contactFormData.block_scopes}
                  onChange={scopes => setContactFormData({ ...contactFormData, block_scopes: scopes })}
                />
                <div>
                  <Label htmlFor="contact_reason">Motivo (opcional)</Label>
                  <Textarea
                    id="contact_reason"
                    placeholder="Ex: Cliente solicitou bloqueio"
                    value={contactFormData.reason || ''}
                    onChange={e => setContactFormData({ ...contactFormData, reason: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving}>
                    {editingContact ? 'Salvar alterações' : 'Adicionar'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetContactForm}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          )}

          {blockedContacts.length > 0 && (
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por número ou motivo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}

          {filteredContacts.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {blockedContacts.length === 0 ? (
                <>
                  <p className="mb-2">Nenhum contato bloqueado.</p>
                  <Button variant="outline" size="sm" onClick={() => setShowContactForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar primeiro contato
                  </Button>
                </>
              ) : (
                <p>Nenhum resultado para a busca.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map(contact => (
                <div
                  key={contact.id}
                  className="flex items-start justify-between py-3 px-3 rounded-lg border bg-white"
                >
                  <div>
                    <p className="font-medium text-gray-900">{contact.phone_number}</p>
                    {contact.reason && <p className="text-sm text-gray-600 mt-0.5">{contact.reason}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      Bloqueio: {formatScopesDisplay(contact.block_scopes)}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEditContact(contact)} title="Editar">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)} title="Remover" className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seção: Por chassi */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              Por chassi (veículo)
            </CardTitle>
            <CardDescription>Veículos que não receberão envios (com escopo opcional)</CardDescription>
          </div>
          <Button
            onClick={() => {
              if (editingVehicle) resetVehicleForm();
              else {
                setEditingVehicle(null);
                setVehicleFormData({ chassi: '', reason: null, block_scopes: null });
                setShowVehicleForm(!showVehicleForm);
              }
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {showVehicleForm && !editingVehicle ? 'Cancelar' : 'Adicionar veículo'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showVehicleForm && (
            <div ref={vehicleFormRef}>
              <form onSubmit={handleSubmitVehicle} className="space-y-4 p-4 border rounded-lg bg-gray-50/50">
                <div>
                  <Label htmlFor="chassi">Chassi *</Label>
                  <Input
                    id="chassi"
                    placeholder="Ex: 9BWZZZ377VT004251"
                    value={vehicleFormData.chassi}
                    onChange={e => setVehicleFormData({ ...vehicleFormData, chassi: e.target.value })}
                    required
                  />
                </div>
                <ScopeCheckboxes
                  idPrefix="vehicle"
                  value={vehicleFormData.block_scopes}
                  onChange={scopes => setVehicleFormData({ ...vehicleFormData, block_scopes: scopes })}
                />
                <div>
                  <Label htmlFor="vehicle_reason">Motivo (opcional)</Label>
                  <Textarea
                    id="vehicle_reason"
                    placeholder="Ex: Veículo em revistoria"
                    value={vehicleFormData.reason || ''}
                    onChange={e => setVehicleFormData({ ...vehicleFormData, reason: e.target.value || null })}
                    rows={2}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving}>
                    {editingVehicle ? 'Salvar alterações' : 'Adicionar'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetVehicleForm}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          )}

          {blockedVehicles.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p className="mb-2">Nenhum veículo bloqueado.</p>
              <Button variant="outline" size="sm" onClick={() => setShowVehicleForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar primeiro veículo
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredVehicles.map(vehicle => (
                <div
                  key={vehicle.id}
                  className="flex items-start justify-between py-3 px-3 rounded-lg border bg-white"
                >
                  <div>
                    <p className="font-medium text-gray-900 font-mono">{vehicle.chassi}</p>
                    {vehicle.reason && <p className="text-sm text-gray-600 mt-0.5">{vehicle.reason}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      Bloqueio: {formatScopesDisplay(vehicle.block_scopes)}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEditVehicle(vehicle)} title="Editar">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteVehicle(vehicle.id)} title="Remover" className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Como funciona</p>
              <p className="text-xs text-blue-800">
                Lembrete = envios até o vencimento. Cobrança = envios pós-vencimento. Revistoria = envios de revistoria.
                Deixe &quot;Bloquear para&quot; em branco para bloquear em todos os tipos. Na importação do lote, o sistema verifica telefones e chassi/código do veículo e marca como BLOQUEADO quando houver correspondência.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlockedContactsTab;
